function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import { createTextVNode as _createTextVNode, mergeProps as _mergeProps, createVNode as _createVNode } from "vue";
import { ref, watch, provide, computed, nextTick, reactive, onMounted, defineComponent } from "vue";
import { isDef, extend, addUnit, toArray, FORM_KEY, numericProp, unknownProp, resetScroll, formatNumber, preventDefault, makeStringProp, makeNumericProp, createNamespace } from "../utils";
import { cutString, runSyncRule, endComposing, mapInputType, startComposing, getRuleMessage, resizeTextarea, getStringLength, runRuleValidator } from "./utils";
import { cellSharedProps } from "../cell/Cell";
import { CUSTOM_FIELD_INJECTION_KEY, useParent } from "@vant/use";
import { useId } from "../composables/use-id";
import { useExpose } from "../composables/use-expose";
import { Icon } from "../icon";
import { Cell } from "../cell";

var _createNamespace = createNamespace("field"),
    _createNamespace2 = _slicedToArray(_createNamespace, 2),
    name = _createNamespace2[0],
    bem = _createNamespace2[1];

var fieldSharedProps = {
  id: String,
  name: String,
  leftIcon: String,
  rightIcon: String,
  autofocus: Boolean,
  clearable: Boolean,
  maxlength: numericProp,
  formatter: Function,
  clearIcon: makeStringProp("clear"),
  modelValue: makeNumericProp(""),
  inputAlign: String,
  placeholder: String,
  autocomplete: String,
  errorMessage: String,
  enterkeyhint: String,
  clearTrigger: makeStringProp("focus"),
  formatTrigger: makeStringProp("onChange"),
  error: {
    type: Boolean,
    "default": null
  },
  disabled: {
    type: Boolean,
    "default": null
  },
  readonly: {
    type: Boolean,
    "default": null
  }
};
var fieldProps = extend({}, cellSharedProps, fieldSharedProps, {
  rows: numericProp,
  type: makeStringProp("text"),
  rules: Array,
  autosize: [Boolean, Object],
  labelWidth: numericProp,
  labelClass: unknownProp,
  labelAlign: String,
  showWordLimit: Boolean,
  errorMessageAlign: String,
  colon: {
    type: Boolean,
    "default": null
  }
});
var stdin_default = defineComponent({
  name: name,
  props: fieldProps,
  emits: ["blur", "focus", "clear", "keypress", "click-input", "click-left-icon", "click-right-icon", "update:modelValue"],
  setup: function setup(props, _ref) {
    var emit = _ref.emit,
        slots = _ref.slots;
    var id = useId();
    var state = reactive({
      focused: false,
      validateFailed: false,
      validateMessage: ""
    });
    var inputRef = ref();
    var customValue = ref();

    var _useParent = useParent(FORM_KEY),
        form = _useParent.parent;

    var getModelValue = function getModelValue() {
      var _a;

      return String((_a = props.modelValue) != null ? _a : "");
    };

    var getProp = function getProp(key) {
      if (isDef(props[key])) {
        return props[key];
      }

      if (form && isDef(form.props[key])) {
        return form.props[key];
      }
    };

    var showClear = computed(function () {
      var readonly = getProp("readonly");

      if (props.clearable && !readonly) {
        var hasValue = getModelValue() !== "";
        var trigger = props.clearTrigger === "always" || props.clearTrigger === "focus" && state.focused;
        return hasValue && trigger;
      }

      return false;
    });
    var formValue = computed(function () {
      if (customValue.value && slots.input) {
        return customValue.value();
      }

      return props.modelValue;
    });

    var runRules = function runRules(rules) {
      return rules.reduce(function (promise, rule) {
        return promise.then(function () {
          if (state.validateFailed) {
            return;
          }

          var value = formValue.value;

          if (rule.formatter) {
            value = rule.formatter(value, rule);
          }

          if (!runSyncRule(value, rule)) {
            state.validateFailed = true;
            state.validateMessage = getRuleMessage(value, rule);
            return;
          }

          if (rule.validator) {
            return runRuleValidator(value, rule).then(function (result) {
              if (result && typeof result === "string") {
                state.validateFailed = true;
                state.validateMessage = result;
              } else if (result === false) {
                state.validateFailed = true;
                state.validateMessage = getRuleMessage(value, rule);
              }
            });
          }
        });
      }, Promise.resolve());
    };

    var resetValidation = function resetValidation() {
      if (state.validateFailed) {
        state.validateFailed = false;
        state.validateMessage = "";
      }
    };

    var validate = function validate() {
      var rules = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : props.rules;
      return new Promise(function (resolve) {
        resetValidation();

        if (rules) {
          runRules(rules).then(function () {
            if (state.validateFailed) {
              resolve({
                name: props.name,
                message: state.validateMessage
              });
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    };

    var validateWithTrigger = function validateWithTrigger(trigger) {
      if (form && props.rules) {
        var validateTrigger = form.props.validateTrigger;
        var defaultTrigger = toArray(validateTrigger).includes(trigger);
        var rules = props.rules.filter(function (rule) {
          if (rule.trigger) {
            return toArray(rule.trigger).includes(trigger);
          }

          return defaultTrigger;
        });

        if (rules.length) {
          validate(rules);
        }
      }
    };

    var limitValueLength = function limitValueLength(value) {
      var maxlength = props.maxlength;

      if (isDef(maxlength) && getStringLength(value) > maxlength) {
        var modelValue = getModelValue();

        if (modelValue && getStringLength(modelValue) === +maxlength) {
          return modelValue;
        }

        return cutString(value, +maxlength);
      }

      return value;
    };

    var updateValue = function updateValue(value) {
      var trigger = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "onChange";
      value = limitValueLength(value);

      if (props.type === "number" || props.type === "digit") {
        var isNumber = props.type === "number";
        value = formatNumber(value, isNumber, isNumber);
      }

      if (props.formatter && trigger === props.formatTrigger) {
        value = props.formatter(value);
      }

      if (inputRef.value && inputRef.value.value !== value) {
        inputRef.value.value = value;
      }

      if (value !== props.modelValue) {
        emit("update:modelValue", value);
      }
    };

    var onInput = function onInput(event) {
      if (!event.target.composing) {
        updateValue(event.target.value);
      }
    };

    var blur = function blur() {
      var _a;

      return (_a = inputRef.value) == null ? void 0 : _a.blur();
    };

    var focus = function focus() {
      var _a;

      return (_a = inputRef.value) == null ? void 0 : _a.focus();
    };

    var adjustTextareaSize = function adjustTextareaSize() {
      var input = inputRef.value;

      if (props.type === "textarea" && props.autosize && input) {
        resizeTextarea(input, props.autosize);
      }
    };

    var onFocus = function onFocus(event) {
      state.focused = true;
      emit("focus", event);
      nextTick(adjustTextareaSize);

      if (getProp("readonly")) {
        blur();
      }
    };

    var onBlur = function onBlur(event) {
      if (getProp("readonly")) {
        return;
      }

      state.focused = false;
      updateValue(getModelValue(), "onBlur");
      emit("blur", event);
      validateWithTrigger("onBlur");
      nextTick(adjustTextareaSize);
      resetScroll();
    };

    var onClickInput = function onClickInput(event) {
      return emit("click-input", event);
    };

    var onClickLeftIcon = function onClickLeftIcon(event) {
      return emit("click-left-icon", event);
    };

    var onClickRightIcon = function onClickRightIcon(event) {
      return emit("click-right-icon", event);
    };

    var onClear = function onClear(event) {
      preventDefault(event);
      emit("update:modelValue", "");
      emit("clear", event);
    };

    var showError = computed(function () {
      if (typeof props.error === "boolean") {
        return props.error;
      }

      if (form && form.props.showError && state.validateFailed) {
        return true;
      }
    });
    var labelStyle = computed(function () {
      var labelWidth = getProp("labelWidth");

      if (labelWidth) {
        return {
          width: addUnit(labelWidth)
        };
      }
    });

    var onKeypress = function onKeypress(event) {
      var ENTER_CODE = 13;

      if (event.keyCode === ENTER_CODE) {
        var submitOnEnter = form && form.props.submitOnEnter;

        if (!submitOnEnter && props.type !== "textarea") {
          preventDefault(event);
        }

        if (props.type === "search") {
          blur();
        }
      }

      emit("keypress", event);
    };

    var getInputId = function getInputId() {
      return props.id || "".concat(id, "-input");
    };

    var renderInput = function renderInput() {
      var controlClass = bem("control", [getProp("inputAlign"), {
        error: showError.value,
        custom: !!slots.input,
        "min-height": props.type === "textarea" && !props.autosize
      }]);

      if (slots.input) {
        return _createVNode("div", {
          "class": controlClass,
          "onClick": onClickInput
        }, [slots.input()]);
      }

      var inputAttrs = {
        id: getInputId(),
        ref: inputRef,
        name: props.name,
        rows: props.rows !== void 0 ? +props.rows : void 0,
        "class": controlClass,
        value: props.modelValue,
        disabled: getProp("disabled"),
        readonly: getProp("readonly"),
        autofocus: props.autofocus,
        placeholder: props.placeholder,
        autocomplete: props.autocomplete,
        enterkeyhint: props.enterkeyhint,
        "aria-labelledby": props.label ? "".concat(id, "-label") : void 0,
        onBlur: onBlur,
        onFocus: onFocus,
        onInput: onInput,
        onClick: onClickInput,
        onChange: endComposing,
        onKeypress: onKeypress,
        onCompositionend: endComposing,
        onCompositionstart: startComposing
      };

      if (props.type === "textarea") {
        return _createVNode("textarea", inputAttrs, null);
      }

      return _createVNode("input", _mergeProps(mapInputType(props.type), inputAttrs), null);
    };

    var renderLeftIcon = function renderLeftIcon() {
      var leftIconSlot = slots["left-icon"];

      if (props.leftIcon || leftIconSlot) {
        return _createVNode("div", {
          "class": bem("left-icon"),
          "onClick": onClickLeftIcon
        }, [leftIconSlot ? leftIconSlot() : _createVNode(Icon, {
          "name": props.leftIcon,
          "classPrefix": props.iconPrefix
        }, null)]);
      }
    };

    var renderRightIcon = function renderRightIcon() {
      var rightIconSlot = slots["right-icon"];

      if (props.rightIcon || rightIconSlot) {
        return _createVNode("div", {
          "class": bem("right-icon"),
          "onClick": onClickRightIcon
        }, [rightIconSlot ? rightIconSlot() : _createVNode(Icon, {
          "name": props.rightIcon,
          "classPrefix": props.iconPrefix
        }, null)]);
      }
    };

    var renderWordLimit = function renderWordLimit() {
      if (props.showWordLimit && props.maxlength) {
        var count = getStringLength(getModelValue());
        return _createVNode("div", {
          "class": bem("word-limit")
        }, [_createVNode("span", {
          "class": bem("word-num")
        }, [count]), _createTextVNode("/"), props.maxlength]);
      }
    };

    var renderMessage = function renderMessage() {
      if (form && form.props.showErrorMessage === false) {
        return;
      }

      var message = props.errorMessage || state.validateMessage;

      if (message) {
        var slot = slots["error-message"];
        var errorMessageAlign = getProp("errorMessageAlign");
        return _createVNode("div", {
          "class": bem("error-message", errorMessageAlign)
        }, [slot ? slot({
          message: message
        }) : message]);
      }
    };

    var renderLabel = function renderLabel() {
      var colon = getProp("colon") ? ":" : "";

      if (slots.label) {
        return [slots.label(), colon];
      }

      if (props.label) {
        return _createVNode("label", {
          "id": "".concat(id, "-label"),
          "for": getInputId()
        }, [props.label + colon]);
      }
    };

    var renderFieldBody = function renderFieldBody() {
      return [_createVNode("div", {
        "class": bem("body")
      }, [renderInput(), showClear.value && _createVNode(Icon, {
        "name": props.clearIcon,
        "class": bem("clear"),
        "onTouchstart": onClear
      }, null), renderRightIcon(), slots.button && _createVNode("div", {
        "class": bem("button")
      }, [slots.button()])]), renderWordLimit(), renderMessage()];
    };

    useExpose({
      blur: blur,
      focus: focus,
      validate: validate,
      formValue: formValue,
      resetValidation: resetValidation
    });
    provide(CUSTOM_FIELD_INJECTION_KEY, {
      customValue: customValue,
      resetValidation: resetValidation,
      validateWithTrigger: validateWithTrigger
    });
    watch(function () {
      return props.modelValue;
    }, function () {
      updateValue(getModelValue());
      resetValidation();
      validateWithTrigger("onChange");
      nextTick(adjustTextareaSize);
    });
    onMounted(function () {
      updateValue(getModelValue(), props.formatTrigger);
      nextTick(adjustTextareaSize);
    });
    return function () {
      var disabled = getProp("disabled");
      var labelAlign = getProp("labelAlign");
      var Label = renderLabel();
      var LeftIcon = renderLeftIcon();
      return _createVNode(Cell, {
        "size": props.size,
        "icon": props.leftIcon,
        "class": bem(_defineProperty({
          error: showError.value,
          disabled: disabled
        }, "label-".concat(labelAlign), labelAlign)),
        "center": props.center,
        "border": props.border,
        "isLink": props.isLink,
        "clickable": props.clickable,
        "titleStyle": labelStyle.value,
        "valueClass": bem("value"),
        "titleClass": [bem("label", [labelAlign, {
          required: props.required
        }]), props.labelClass],
        "arrowDirection": props.arrowDirection
      }, {
        icon: LeftIcon ? function () {
          return LeftIcon;
        } : null,
        title: Label ? function () {
          return Label;
        } : null,
        value: renderFieldBody,
        extra: slots.extra
      });
    };
  }
});
export { stdin_default as default, fieldSharedProps };